import { MapPin, Phone, Mail, Globe, MessageCircle, FileCheck2 } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CONTACT, NAV_ITEMS, COMPLIANCE_NOTE } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { waLink } from "@/lib/contactLinks";
import Brand from "@/components/site/Brand";

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const goSection = (href) => {
    if (location.pathname !== "/") navigate(`/#${href}`);
    else scrollToId(href);
  };

  return (
    <footer className="relative border-t border-white/10 bg-[#03101d]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <Brand onClick={() => navigate("/")} badgeSize="h-11 w-11" nameClass="text-lg sm:text-xl" />
            <p className="mt-4 max-w-md text-sm text-slate-400 leading-relaxed">
              {CONTACT.company}. An Indian aquaculture &amp; food-products company — connecting quality Indian
              products with global markets. Current export focus: Makhana (Fox Nuts).
            </p>
            <div className="mt-5 space-y-3 text-sm text-slate-400">
              <p className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-cyan-400 shrink-0" /><span><span className="text-slate-300 font-medium">Registered Office:</span> {CONTACT.office}</span></p>
              <p className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" /><span><span className="text-slate-300 font-medium">Farm / Project:</span> {CONTACT.farm}</span></p>
            </div>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1.5 text-xs text-slate-500 font-mono-tech">
              <span><span className="text-slate-400">CIN:</span> {CONTACT.cin}</span>
              <span><span className="text-slate-400">PAN:</span> {CONTACT.pan}</span>
              <span><span className="text-slate-400">TAN:</span> {CONTACT.tan}</span>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Explore</h4>
            <ul className="mt-4 space-y-2.5">
              {NAV_ITEMS.map((n) => (
                <li key={n.label}>
                  {n.type === "route" ? (
                    <Link to={n.to} className="text-sm text-slate-400 hover:text-white transition-colors">{n.label}</Link>
                  ) : (
                    <button onClick={() => goSection(n.href)} className="text-sm text-slate-400 hover:text-white transition-colors">{n.label}</button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Get in touch</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li><a href={waLink()} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp: {CONTACT.whatsapp}</a></li>
              <li><a href={`tel:${CONTACT.ceoPhone}`} className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors"><Phone className="h-4 w-4 text-cyan-400" /> {CONTACT.ceoPhone}</a></li>
              {CONTACT.emails.map((e) => (
                <li key={e}><a href={`mailto:${e}`} className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors break-all"><Mail className="h-4 w-4 text-emerald-400" /> {e}</a></li>
              ))}
              {CONTACT.websites.map((w) => (
                <li key={w}><a href={`https://${w}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors"><Globe className="h-4 w-4 text-amber-400" /> {w}</a></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 flex items-start gap-3 rounded-2xl border border-white/10 bg-[#071728]/60 p-4">
          <FileCheck2 className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
          <p className="text-xs text-slate-400 leading-relaxed"><span className="font-semibold text-slate-200">Quality &amp; Export Compliance:</span> {COMPLIANCE_NOTE}</p>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} {CONTACT.company}. All rights reserved.</p>
          <p className="text-xs text-slate-500 font-mono-tech">Aquaculture · Food Products · Export</p>
        </div>
      </div>
    </footer>
  );
}
